/**
 * The client port of `internal/floorplan/validate.go` (spec 2026-09-05-07 §5a).
 *
 * The job's report is what tells the user their extraction is bad. This is what
 * clears it: the same eleven checks, over the *edited* document, so a badge
 * disappears the moment the user fixes the element instead of after another
 * half-minute run. `Promote all` is disabled while any `error` remains, and
 * that gate reads this, not the stored report.
 *
 * The one deliberate difference from the Go is the coordinate system: the
 * editor works in the frame's own plan units, so everything here is scaled
 * through `mmPerUnit` rather than `mmPerPx`. An uncalibrated frame (which §2
 * now prevents for a digitized bundle, but which a hand-drawn one can still be)
 * yields **no** length-based issue rather than a screenful of nonsense — that is
 * the §0 finding that the existing `validate()` compared plan units against
 * millimetre constants and reported "Wall shorter than 50 mm (0 mm)" thirty
 * times on every uncalibrated frame.
 *
 * Pure TypeScript — no React, no DOM, unit-tested with vitest.
 */
import { dist } from './geometry'
import type { Point } from './geometry'
import type { EditorElement, SceneDoc } from './scene'

export type FloorplanIssueSeverity = 'error' | 'warning'

export interface FloorplanIssue {
    code: string
    severity: FloorplanIssueSeverity
    /** The element the issue points at, so the inspector can list it. */
    elementKey?: string
    /** Numbers the translated message interpolates. */
    params?: Record<string, string | number>
}

/* The thresholds, verbatim from the Go. Millimetres, square millimetres, ratios. */
const MIN_ROOM_AREA_MM2 = 1_000_000
const MIN_SMALL_ROOM_AREA_MM2 = 300_000
const MAX_ROOM_AREA_MM2 = 150_000_000
const MIN_WALL_LENGTH_MM = 150
const MAX_WALL_LENGTH_MM = 40_000
const MIN_DOOR_WIDTH_MM = 550
const MAX_DOOR_WIDTH_MM = 1_600
const MIN_WINDOW_WIDTH_MM = 300
const MAX_WINDOW_WIDTH_MM = 4_000
const MIN_TOTAL_AREA_MM2 = 8_000_000
const MAX_TOTAL_AREA_MM2 = 3_000_000_000
const OVERLAP_FRACTION = 0.2
const BOUNDED_FRACTION = 0.8
const COVERAGE_FRACTION = 0.5
/** The post-processor's own centreline-to-room-edge tolerance. */
const PERP_TOL_MM = 120

/** Space types whose lower area bound is the small one. */
const SMALL_SPACE_TYPES = new Set(['balcony', 'closet', 'terrace', 'loggia', 'toilet', 'storage', 'cellar'])

/**
 * Every issue code, in the order the Go declares them — the i18n bundles key
 * `planEditor.floorplanIssues.<code>` off exactly these.
 */
export const FLOORPLAN_ISSUE_CODES = [
    'space_not_simple',
    'space_area_out_of_range',
    'spaces_overlap',
    'space_unbounded',
    'wall_floating',
    'wall_length_out_of_range',
    'opening_off_wall',
    'opening_width_out_of_range',
    'label_missing',
    'coverage_low',
    'scale_implausible',
] as const

/**
 * Validate the *digitized* geometry of a document.
 *
 * @param mmPerUnit the frame's calibration; `null` disables every length check.
 * @param repUids when given, only elements from these representations are
 *   checked — the digitized bundle, not the whole frame's hand-drawn history.
 * @param expectedLabels room names the frame is known to carry, for
 *   `label_missing`.
 */
export function validateFloorplan(
    doc: SceneDoc,
    mmPerUnit: number | null,
    repUids?: Set<string>,
    expectedLabels: string[] = [],
): FloorplanIssue[] {
    const issues: FloorplanIssue[] = []
    const inScope = (el: EditorElement) => !repUids || (!!el.source.repUid && repUids.has(el.source.repUid))
    const elements = doc.elements.filter(inScope)
    const spaces = elements.filter((el): el is Extract<EditorElement, { kind: 'space' }> => el.kind === 'space')
    const walls = elements.filter((el): el is Extract<EditorElement, { kind: 'wall' }> => el.kind === 'wall')
    const openings = elements.filter((el): el is Extract<EditorElement, { kind: 'opening' }> => el.kind === 'opening')

    const scaled = typeof mmPerUnit === 'number' && Number.isFinite(mmPerUnit) && mmPerUnit > 0
    const k = scaled ? (mmPerUnit as number) : 1
    const mm = (units: number) => units * k
    const mm2 = (units: number) => units * k * k
    const perpUnits = PERP_TOL_MM / k

    // --- spaces ---------------------------------------------------------------
    let total = 0
    const rings = new Map<string, Point[]>()
    // A space's holes (spec 2026-10-19-07) are not its floor: they come off its
    // area, and a room drawn in one does not overlap the room around it.
    const holesOf = new Map<string, Point[][]>()
    for (const sp of spaces) {
        const ring = dedupeRing(sp.boundary)
        if (ring.length < 3 || selfIntersects(ring)) {
            issues.push({
                code: 'space_not_simple', severity: 'error', elementKey: sp.key,
                params: { name: spaceLabel(sp), count: ring.length },
            })
            continue
        }
        rings.set(sp.key, ring)
        const holes = (sp.holes ?? []).map(dedupeRing).filter((h) => h.length >= 3)
        holesOf.set(sp.key, holes)
        const areaMM2 = mm2(Math.max(0, holes.reduce((a, h) => a - polygonArea(h), polygonArea(ring))))
        total += areaMM2
        if (!scaled) continue
        const low = SMALL_SPACE_TYPES.has(sp.spaceTypeHint ?? '') ? MIN_SMALL_ROOM_AREA_MM2 : MIN_ROOM_AREA_MM2
        if (areaMM2 < low || areaMM2 > MAX_ROOM_AREA_MM2) {
            issues.push({
                code: 'space_area_out_of_range', severity: 'warning', elementKey: sp.key,
                params: {
                    name: spaceLabel(sp),
                    area: round2(areaMM2 / 1e6),
                    low: round2(low / 1e6),
                    high: Math.round(MAX_ROOM_AREA_MM2 / 1e6),
                },
            })
        }
    }

    // --- overlapping spaces ---------------------------------------------------
    const keys = [...rings.keys()]
    for (let i = 0; i < keys.length; i++) {
        const a = rings.get(keys[i]) as Point[]
        const holesA = holesOf.get(keys[i]) ?? []
        const areaA = polygonArea(a)
        for (let j = i + 1; j < keys.length; j++) {
            const b = rings.get(keys[j]) as Point[]
            const smaller = Math.min(areaA, polygonArea(b))
            if (smaller <= 0) continue
            const pct = (overlapArea(a, b, holesA, holesOf.get(keys[j]) ?? []) / smaller) * 100
            if (pct / 100 > OVERLAP_FRACTION) {
                issues.push({
                    code: 'spaces_overlap', severity: 'error', elementKey: keys[i],
                    params: {
                        name: spaceLabel(spaces.find((s) => s.key === keys[i])),
                        other: spaceLabel(spaces.find((s) => s.key === keys[j])),
                        percent: Math.round(pct),
                    },
                })
            }
        }
    }

    // --- spaces without walls around them -------------------------------------
    for (const sp of spaces) {
        const ring = rings.get(sp.key)
        if (!ring) continue
        const [covered, perimeter] = walledPerimeter(ring, walls, 2 * perpUnits)
        if (perimeter <= 0) continue
        const frac = covered / perimeter
        if (frac < BOUNDED_FRACTION) {
            issues.push({
                code: 'space_unbounded', severity: 'warning', elementKey: sp.key,
                params: { name: spaceLabel(sp), percent: Math.round(frac * 100) },
            })
        }
    }

    // --- walls ----------------------------------------------------------------
    for (const w of walls) {
        if (scaled) {
            const lenMM = mm(dist(w.start, w.end))
            if (lenMM < MIN_WALL_LENGTH_MM || lenMM > MAX_WALL_LENGTH_MM) {
                issues.push({
                    code: 'wall_length_out_of_range', severity: 'warning', elementKey: w.key,
                    params: {
                        id: w.source.localId,
                        length: round2(lenMM / 1000),
                        low: round2(MIN_WALL_LENGTH_MM / 1000),
                        high: Math.round(MAX_WALL_LENGTH_MM / 1000),
                    },
                })
            }
        }
        if (wallFloats(w, walls, spaces, perpUnits)) {
            issues.push({
                code: 'wall_floating', severity: 'warning', elementKey: w.key,
                params: { id: w.source.localId },
            })
        }
    }

    // --- openings -------------------------------------------------------------
    const wallByKey = new Map(walls.map((w) => [w.key, w]))
    for (const op of openings) {
        const host = op.wallKey ? wallByKey.get(op.wallKey) : undefined
        if (!host) {
            issues.push({
                code: 'opening_off_wall', severity: 'error', elementKey: op.key,
                params: { kind: op.openingKind, id: op.source.localId, wall: op.wallRef?.wallId ?? '—' },
            })
        } else {
            const d = pointSegmentDistance(op.center, host.start, host.end)
            const limit = Math.max(perpUnits, host.thickness)
            if (d > limit) {
                issues.push({
                    code: 'opening_off_wall', severity: 'error', elementKey: op.key,
                    params: {
                        kind: op.openingKind, id: op.source.localId,
                        wall: host.source.localId, distance: round2(mm(d) / 1000),
                    },
                })
            }
        }
        if (!scaled || op.width <= 0) continue
        const wMM = mm(op.width)
        const isWindow = op.openingKind === 'window'
        const low = isWindow ? MIN_WINDOW_WIDTH_MM : MIN_DOOR_WIDTH_MM
        const high = isWindow ? MAX_WINDOW_WIDTH_MM : MAX_DOOR_WIDTH_MM
        if (wMM < low || wMM > high) {
            issues.push({
                code: 'opening_width_out_of_range', severity: 'warning', elementKey: op.key,
                params: {
                    kind: op.openingKind, id: op.source.localId,
                    width: round2(wMM / 1000), low: round2(low / 1000), high: round2(high / 1000),
                },
            })
        }
    }

    // --- labels the scan carried but the bundle lacks --------------------------
    if (expectedLabels.length) {
        const drawn = new Set(spaces.map((sp) => foldLabel(sp.name)))
        const missing = expectedLabels
            .filter((label) => {
                const folded = foldLabel(label)
                return folded !== '' && !drawn.has(folded)
            })
            .sort()
        for (const label of missing) {
            issues.push({ code: 'label_missing', severity: 'warning', params: { label } })
        }
    }

    // --- coverage --------------------------------------------------------------
    const hull = convexHull(walls.flatMap((w) => [w.start, w.end]))
    if (hull.length >= 3) {
        const hullArea = polygonArea(hull)
        let spaceArea = 0
        for (const ring of rings.values()) spaceArea += polygonArea(ring)
        if (hullArea > 0 && spaceArea / hullArea < COVERAGE_FRACTION) {
            issues.push({
                code: 'coverage_low', severity: 'warning',
                params: { percent: Math.round((spaceArea / hullArea) * 100) },
            })
        }
    }

    // --- the scale itself -------------------------------------------------------
    if (scaled && spaces.length > 0 && (total < MIN_TOTAL_AREA_MM2 || total > MAX_TOTAL_AREA_MM2)) {
        issues.push({
            code: 'scale_implausible', severity: 'error',
            params: {
                area: round1(total / 1e6),
                low: Math.round(MIN_TOTAL_AREA_MM2 / 1e6),
                high: Math.round(MAX_TOTAL_AREA_MM2 / 1e6),
            },
        })
    }

    return issues
}

/** True while any `error` remains — what gates **Promote all**. */
export const hasFloorplanErrors = (issues: FloorplanIssue[]): boolean =>
    issues.some((i) => i.severity === 'error')

/** Issues indexed by the element they point at. */
export function floorplanIssuesByElement(issues: FloorplanIssue[]): Map<string, FloorplanIssue[]> {
    const out = new Map<string, FloorplanIssue[]>()
    for (const issue of issues) {
        if (!issue.elementKey) continue
        const list = out.get(issue.elementKey)
        if (list) list.push(issue)
        else out.set(issue.elementKey, [issue])
    }
    return out
}

/* ------------------------------------------------------------------ geometry */

function spaceLabel(sp: { name?: string; source?: { localId: string } } | undefined): string {
    return sp?.name?.trim() || sp?.source?.localId || '—'
}

/** The Go's `FoldLabel`: accents stripped, lowercased, separators collapsed. */
export function foldLabel(s: string): string {
    return (s ?? '')
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, ' ')
        .trim()
}

function samePoint(a: Point, b: Point): boolean {
    return Math.abs(a.x - b.x) < 1e-9 && Math.abs(a.y - b.y) < 1e-9
}

function dedupeRing(ring: Point[]): Point[] {
    const out: Point[] = []
    for (const p of ring) {
        if (out.length && samePoint(out[out.length - 1], p)) continue
        out.push(p)
    }
    while (out.length > 1 && samePoint(out[0], out[out.length - 1])) out.pop()
    return out
}

function polygonArea(ring: Point[]): number {
    if (ring.length < 3) return 0
    let s = 0
    for (let i = 0; i < ring.length; i++) {
        const j = (i + 1) % ring.length
        s += ring[i].x * ring[j].y - ring[j].x * ring[i].y
    }
    return Math.abs(s) / 2
}

function cross(o: Point, a: Point, b: Point): number {
    return (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x)
}

function segmentsCross(p1: Point, p2: Point, p3: Point, p4: Point): boolean {
    const d1 = cross(p3, p4, p1)
    const d2 = cross(p3, p4, p2)
    const d3 = cross(p1, p2, p3)
    const d4 = cross(p1, p2, p4)
    return ((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) && ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0))
}

function selfIntersects(ring: Point[]): boolean {
    const n = ring.length
    for (let i = 0; i < n; i++) {
        const a1 = ring[i]
        const a2 = ring[(i + 1) % n]
        for (let j = i + 1; j < n; j++) {
            if (j === i || (j + 1) % n === i || j === (i + 1) % n) continue
            if (segmentsCross(a1, a2, ring[j], ring[(j + 1) % n])) return true
        }
    }
    return false
}

function ringBounds(ring: Point[]): [number, number, number, number] {
    let x0 = ring[0].x
    let y0 = ring[0].y
    let x1 = x0
    let y1 = y0
    for (const p of ring) {
        x0 = Math.min(x0, p.x)
        y0 = Math.min(y0, p.y)
        x1 = Math.max(x1, p.x)
        y1 = Math.max(y1, p.y)
    }
    return [x0, y0, x1, y1]
}

function pointInRing(p: Point, ring: Point[]): boolean {
    let inside = false
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i, i++) {
        const pi = ring[i]
        const pj = ring[j]
        if ((pi.y > p.y) !== (pj.y > p.y)) {
            const x = pi.x + ((p.y - pi.y) / (pj.y - pi.y)) * (pj.x - pi.x)
            if (p.x < x) inside = !inside
        }
    }
    return inside
}

/**
 * Sampled intersection area, as in the Go: exact polygon clipping is a
 * dependency neither side needs for a "more than 20 % of the smaller one"
 * verdict. A sample in either space's hole is in neither: the Go never sees a
 * hole — a digitization does not draw any — but an edited bundle can.
 */
function overlapArea(a: Point[], b: Point[], holesA: Point[][] = [], holesB: Point[][] = []): number {
    const [ax0, ay0, ax1, ay1] = ringBounds(a)
    const [bx0, by0, bx1, by1] = ringBounds(b)
    const x0 = Math.max(ax0, bx0)
    const y0 = Math.max(ay0, by0)
    const x1 = Math.min(ax1, bx1)
    const y1 = Math.min(ay1, by1)
    if (x1 <= x0 || y1 <= y0) return 0
    const steps = 48
    const dx = (x1 - x0) / steps
    const dy = (y1 - y0) / steps
    const cell = dx * dy
    let total = 0
    for (let i = 0; i < steps; i++) {
        for (let j = 0; j < steps; j++) {
            const p = { x: x0 + (i + 0.5) * dx, y: y0 + (j + 0.5) * dy }
            if (pointInRing(p, a) && pointInRing(p, b)
                && !holesA.some((h) => pointInRing(p, h)) && !holesB.some((h) => pointInRing(p, h))) total += cell
        }
    }
    return total
}

function pointSegmentDistance(p: Point, a: Point, b: Point): number {
    const vx = b.x - a.x
    const vy = b.y - a.y
    const len2 = vx * vx + vy * vy
    if (len2 <= 0) return Math.hypot(p.x - a.x, p.y - a.y)
    const t = Math.max(0, Math.min(1, ((p.x - a.x) * vx + (p.y - a.y) * vy) / len2))
    return Math.hypot(p.x - (a.x + t * vx), p.y - (a.y + t * vy))
}

function walledPerimeter(
    ring: Point[],
    walls: { start: Point; end: Point }[],
    tol: number,
): [number, number] {
    let covered = 0
    let perimeter = 0
    for (let i = 0; i < ring.length; i++) {
        const a = ring[i]
        const b = ring[(i + 1) % ring.length]
        const l = Math.hypot(b.x - a.x, b.y - a.y)
        if (l <= 0) continue
        perimeter += l
        const steps = Math.max(4, Math.min(64, Math.floor(l / Math.max(tol, 1e-9))))
        let hits = 0
        for (let s = 0; s < steps; s++) {
            const t = (s + 0.5) / steps
            const p = { x: a.x + t * (b.x - a.x), y: a.y + t * (b.y - a.y) }
            if (walls.some((w) => pointSegmentDistance(p, w.start, w.end) <= tol)) hits++
        }
        covered += (l * hits) / steps
    }
    return [covered, perimeter]
}

function wallFloats(
    wall: { key: string; start: Point; end: Point },
    walls: { key: string; start: Point; end: Point }[],
    spaces: { boundary: Point[] }[],
    tol: number,
): boolean {
    const touches = (p: Point) => {
        for (const other of walls) {
            if (other.key === wall.key) continue
            if (pointSegmentDistance(p, other.start, other.end) <= tol) return true
        }
        for (const sp of spaces) {
            const ring = sp.boundary
            for (let i = 0; i < ring.length; i++) {
                if (pointSegmentDistance(p, ring[i], ring[(i + 1) % ring.length]) <= tol) return true
            }
        }
        return false
    }
    return !touches(wall.start) && !touches(wall.end)
}

function convexHull(pts: Point[]): Point[] {
    if (pts.length < 3) return []
    const p = [...pts].sort((a, b) => (a.x !== b.x ? a.x - b.x : a.y - b.y))
    const build = (src: Point[]): Point[] => {
        const out: Point[] = []
        for (const pt of src) {
            while (out.length >= 2 && cross(out[out.length - 2], out[out.length - 1], pt) <= 0) out.pop()
            out.push(pt)
        }
        return out
    }
    const lower = build(p)
    const upper = build([...p].reverse())
    if (lower.length < 2 || upper.length < 2) return []
    return [...lower.slice(0, -1), ...upper.slice(0, -1)]
}

const round1 = (v: number) => Math.round(v * 10) / 10
const round2 = (v: number) => Math.round(v * 100) / 100
